import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Landing from "@/pages/Landing";
import Install from "@/pages/Install";
import Onboarding from "@/pages/Onboarding";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Preferences from "@/pages/Preferences";
import Discovery from "@/pages/Discovery";
import SelectSeven from "@/pages/SelectSeven";
import TournamentDashboard from "@/pages/TournamentDashboard";
import RoundChallenge from "@/pages/RoundChallenge";
import HeadToHead from "@/pages/HeadToHead";
import SoftTransition from "@/pages/SoftTransition";
import CompatibilityBreakdown from "@/pages/CompatibilityBreakdown";
import Bracket from "@/pages/Bracket";
import FinalistComparison from "@/pages/FinalistComparison";
import WinnerReveal from "@/pages/WinnerReveal";
import Chat from "@/pages/Chat";
import ProfileDetail from "@/pages/ProfileDetail";
import Settings from "@/pages/Settings";
import Membership from "@/pages/Membership";

import Guide from "@/pages/Guide";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Safety from "@/pages/Safety";
import Presence from "@/pages/Presence";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminMetrics from "@/pages/admin/AdminMetrics";
import AdminHeatmap from "@/pages/admin/AdminHeatmap";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminPrivateApplications from "@/pages/admin/AdminPrivateApplications";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminIntelligence from "@/pages/admin/AdminIntelligence";
import AdminMediaModeration from "@/pages/admin/AdminMediaModeration";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import Concierge from "@/pages/Concierge";
import Profile from "@/pages/Profile";
import ScrollToTop from "@/components/ScrollToTop";
import AdminShell from "@/components/AdminShell";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  componentDidCatch(error, info) {
    this.setState({ error, info });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ background: "#0a0a0a", minHeight: "100vh", padding: 40, fontFamily: "monospace" }}>
          <div style={{ color: "#ff4444", fontSize: 20, marginBottom: 16 }}>Admin dashboard crashed</div>
          <div style={{ color: "#ffaaaa", marginBottom: 12 }}>{this.state.error.message}</div>
          {this.state.info && (
            <pre style={{ color: "#aaaaaa", fontSize: 12, whiteSpace: "pre-wrap" }}>
              {this.state.info.componentStack}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ user, children }) {
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && user.role !== "owner") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user, authLoaded } = useAuth();

  if (!authLoaded) {
    return <div style={{ padding: 40, color: "white" }}>Loading...</div>;
  }

  return (
    <Routes>
      {/* Marketing / public */}
      <Route path="/install" element={<Install />} />
      <Route path="/" element={<Landing />} />
      <Route path="/guide" element={<Guide />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/safety" element={<Safety />} />
      <Route path="/membership" element={<Membership />} />

      {/* Auth entry */}
      <Route
        path="/onboarding"
        element={<Onboarding />}
      />
        <Route
          path="/login"
          element={user ? <Navigate to={(user.role === "admin" || user.role === "owner") ? "/admin/dashboard" : "/dashboard"} replace /> : <Login />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected app routes */}
      <Route
        path="/preferences"
        element={
          <ProtectedRoute user={user}>
            <Preferences />
          </ProtectedRoute>
        }
      />
      <Route
        path="/discovery"
        element={
          <ProtectedRoute user={user}>
            <Discovery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/select-seven"
        element={
          <ProtectedRoute user={user}>
            <SelectSeven />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          (user?.role === "admin" || user?.role === "owner")
            ? <Navigate to="/admin/dashboard" replace />
            : <ProtectedRoute user={user}><TournamentDashboard /></ProtectedRoute>
        }
      />
      <Route
        path="/round"
        element={
          <ProtectedRoute user={user}>
            <RoundChallenge />
          </ProtectedRoute>
        }
      />
      <Route
        path="/soft-transition"
        element={
          <ProtectedRoute user={user}>
            <SoftTransition />
          </ProtectedRoute>
        }
      />
      <Route
        path="/head-to-head"
        element={
          <ProtectedRoute user={user}>
            <HeadToHead />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compatibility"
        element={
          <ProtectedRoute user={user}>
            <CompatibilityBreakdown />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bracket"
        element={
          <ProtectedRoute user={user}>
            <Bracket />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finalists"
        element={
          <ProtectedRoute user={user}>
            <FinalistComparison />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reveal"
        element={
          <ProtectedRoute user={user}>
            <WinnerReveal />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute user={user}>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:id"
        element={
          <ProtectedRoute user={user}>
            <ProfileDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute user={user}>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute user={user}>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/presence"
        element={
          <ProtectedRoute user={user}>
            <Presence />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
        <Route
        path="/admin/dashboard"
        element={
          <AdminRoute user={user}>
            <ErrorBoundary>
              <AdminDashboard />
            </ErrorBoundary>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute user={user}>
            <AdminUsers />
          </AdminRoute>
        }
      />
        <Route
          path="/admin/moderation"
          element={
            <AdminRoute user={user}>
              <AdminMediaModeration />
            </AdminRoute>
          }
        />
        <Route
        path="/admin/metrics"
        element={
          <AdminRoute user={user}>
            <AdminMetrics />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/heatmap"
        element={
          <AdminRoute user={user}>
            <AdminHeatmap />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/messages"
        element={
          <AdminRoute user={user}>
            <AdminMessages />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/private-applications"
        element={
          <AdminRoute user={user}>
            <AdminPrivateApplications />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <AdminRoute user={user}>
            <AdminAnalytics />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/intelligence"
        element={
          <AdminRoute user={user}>
            <AdminIntelligence />
          </AdminRoute>
        }
      />

      {/* Protected: concierge (gate enforced server-side) */}
      <Route
        path="/concierge"
        element={
          <ProtectedRoute user={user}>
            <Concierge />
          </ProtectedRoute>
        }
      />

      {/* fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <div className="App font-body">
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}
